import type { AxiosInstance, AxiosResponse } from 'axios';
import axios from 'axios';
import type { ServiceResultT } from '../types/common/ServiceResultT';
import type { ServiceResult } from '../types/common/ServiceResult';
import type { ListingViewModel } from '../types/listings/ListingViewModel';
import type { ListingQueryModel } from '../types/listings/ListingQueryModel';
import type { MarkListingAsSoldRequest } from '../types/listings/MarkListingAsSoldRequest';
import { buildListingQueryParams } from '../client/queryParams';
import { apiClient } from '../client/client';

/**
 * Swagger marks some PUT endpoints as 200 OK with no body. Axios then yields undefined/'' as data.
 * Treat 2xx as success; if body is a ServiceResult JSON, use it as-is.
 */
function serviceResultTFromResponse<T>(response: AxiosResponse): ServiceResultT<T> {
  const payload = response.data;
  if (payload != null && typeof payload === 'object' && 'isSuccess' in payload) {
    return payload as ServiceResultT<T>;
  }
  const ok = response.status >= 200 && response.status < 300;
  if (ok) {
    const data =
      payload != null && typeof payload === 'object' ? (payload as T) : null;
    return { isSuccess: true, statusCode: response.status, data };
  }
  return {
    isSuccess: false,
    statusCode: response.status,
    message: 'İstek başarısız',
  };
}

/**
 * Listing service – create/update use multipart/form-data (File objects, no Base64).
 * Axios detects FormData and sets headers; preserve file streams.
 */
export class ListingService {
  private static readonly BASE = '/api/listing';

  constructor(private readonly client: AxiosInstance = apiClient) {}

  async getAll(query?: ListingQueryModel): Promise<ServiceResultT<ListingViewModel[]>> {
    const params = buildListingQueryParams(query);
    const { data } = await this.client.get<ServiceResultT<ListingViewModel[]>>(
      ListingService.BASE,
      { params }
    );
    return data;
  }

  async getAllActive(query?: ListingQueryModel): Promise<ServiceResultT<ListingViewModel[]>> {
    const params = buildListingQueryParams(query);
    const { data } = await this.client.get<ServiceResultT<ListingViewModel[]>>(
      `${ListingService.BASE}/active`,
      { params }
    );
    return data;
  }

  async getById(id: string): Promise<ServiceResultT<ListingViewModel>> {
    const { data } = await this.client.get<ServiceResultT<ListingViewModel>>(
      `${ListingService.BASE}/${id}`
    );
    return data;
  }

  /** POST multipart/form-data: Title, Description, ConditionId, ProductTypeId, CityId, DistrictId, Price, HasWarranty, IsTested, Specs (JSON string?), Images (File[]). */
  /** Axios detects FormData and sets Content-Type to multipart/form-data with boundary. */
  async create(formData: FormData): Promise<ServiceResultT<ListingViewModel>> {
    const { data } = await this.client.post<ServiceResultT<ListingViewModel>>(
      ListingService.BASE,
      formData
    );
    return data;
  }

  /** PUT multipart/form-data: Title, Description, ..., RemovedImageUrls, NewImages (File[]). */
  async update(id: string, formData: FormData): Promise<ServiceResultT<ListingViewModel>> {
    const { data } = await this.client.put<ServiceResultT<ListingViewModel>>(
      `${ListingService.BASE}/${id}`,
      formData
    );
    return data;
  }

  async delete(id: string): Promise<ServiceResult> {
    const { data } = await this.client.delete<ServiceResult>(`${ListingService.BASE}/${id}`);
    return data;
  }

  async getByUserId(userId: string): Promise<ServiceResultT<ListingViewModel[]>> {
    const { data } = await this.client.get<ServiceResultT<ListingViewModel[]>>(
      `${ListingService.BASE}/user/${userId}`,
      { params: {} }
    );
    return data;
  }

  /** PUT /api/listing/make-listing-tested/{id} — marks listing as tested (admin). May return empty 200. */
  async makeListingTested(id: string): Promise<ServiceResultT<ListingViewModel>> {
    try {
      const response = await this.client.put(
        `${ListingService.BASE}/make-listing-tested/${id}`
      );
      return serviceResultTFromResponse<ListingViewModel>(response);
    } catch (err) {
      if (axios.isAxiosError(err)) {
        const status = err.response?.status ?? 0;
        const body = err.response?.data;
        let message: string | undefined;
        if (body != null && typeof body === 'object' && 'message' in body) {
          message = String((body as { message?: unknown }).message ?? '');
        }
        return {
          isSuccess: false,
          statusCode: status,
          message: message?.trim() || err.message || undefined,
        };
      }
      throw err;
    }
  }

  async incrementView(id: string): Promise<ServiceResult> {
    const { data } = await this.client.post<ServiceResult>(
      `${ListingService.BASE}/${id}/increment-view`
    );
    return data;
  }

  /**
   * POST /api/listing/{id}/mark-as-sold
   */
  async markAsSold(
    listingId: string,
    payload: MarkListingAsSoldRequest
  ): Promise<ServiceResult> {
    const { data } = await this.client.post<ServiceResult>(
      `${ListingService.BASE}/${listingId}/mark-as-sold`,
      payload
    );
    return data;
  }
}

export const listingService = new ListingService();
